import { useEffect, useState } from "react";
import type { PlaybackUrlResponseDto, ViewerVideoDetailDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { createFamilyPlaybackUrl, fetchFamilyVideoDetail } from "../../shared/api/video";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const familyLinks = [
  { to: "/t/:tenantSlug/home", label: "ホーム" },
  { to: "/t/:tenantSlug/videos", label: "動画一覧" },
  { to: "/t/:tenantSlug/notifications", label: "通知" }
];

export function FamilyVideoDetailPage() {
  const { tenantSlug = "unknown-tenant", videoId = "" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [video, setVideo] = useState<ViewerVideoDetailDto | null>(null);
  const [playback, setPlayback] = useState<PlaybackUrlResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [playbackState, setPlaybackState] = useState<"idle" | "loading">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole || !videoId) {
      return;
    }

    let cancelled = false;

    async function loadVideo() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextVideo = await fetchFamilyVideoDetail(tenantSlug, videoId);

        if (cancelled) {
          return;
        }

        setVideo(nextVideo);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "video detail の取得に失敗しました");
      }
    }

    void loadVideo();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, tenantSlug, videoId]);

  async function handlePlayback() {
    if (!videoId) {
      return;
    }

    setPlaybackState("loading");
    setLoadError(null);

    try {
      const nextPlayback = await createFamilyPlaybackUrl(tenantSlug, videoId);
      setPlayback(nextPlayback);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "playback url の取得に失敗しました");
    } finally {
      setPlaybackState("idle");
    }
  }

  return (
    <PageScaffold
      title="Family Video Detail"
      description="family 向けの動画 detail と playback-url 取得画面です。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            family 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock family login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasParentRole ? <p>この画面は parent role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>video detail を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {video ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{video.title ?? "タイトル未設定"}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {video.videoPurpose} / {video.targetType} / {video.targetLabel}
              </div>
              {video.description ? <div style={{ fontSize: 14 }}>{video.description}</div> : null}
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                公開期間: {video.publishFrom ? video.publishFrom.slice(0, 10) : "未設定"} - {video.publishTo ? video.publishTo.slice(0, 10) : "未設定"}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                playbackAvailable: {String(video.playbackAvailable)}
              </div>
            </section>

            <button type="button" onClick={() => void handlePlayback()} disabled={playbackState === "loading"}>
              {playbackState === "loading" ? "取得中..." : "Playback URL を取得"}
            </button>

            {playback ? (
              <section style={{ display: "grid", gap: 8 }}>
                <strong>Fake Playback URL</strong>
                <a href={playback.playbackUrl} target="_blank" rel="noreferrer">
                  {playback.playbackUrl}
                </a>
                <div style={{ fontSize: 14, opacity: 0.8 }}>expiresAt: {playback.expiresAt}</div>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
